import type { Partner } from '@/api/partners';
import { useApiError } from '@/hooks/use-api-error';
import { useCheckPartnerDuplicate, useQuickCreatePartner } from '@/hooks/use-partners';
import { useTranslation } from '@kgc/i18n';
import type { ExtractedIdFields } from '@kgc/tesseract-provider';
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@kgc/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScanIdCardButton } from './ScanIdCardButton';

interface QuickCreatePartnerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (partner: Partner) => void;
  initialName?: string;
}

export function QuickCreatePartnerDrawer({
  open,
  onOpenChange,
  onCreated,
  initialName = '',
}: QuickCreatePartnerDrawerProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const { create, isLoading } = useQuickCreatePartner();
  const { duplicates, check, clear } = useCheckPartnerDuplicate();

  const [type, setType] = useState<'INDIVIDUAL' | 'COMPANY'>('INDIVIDUAL');
  const [name, setName] = useState(initialName);
  const [taxNumber, setTaxNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  // Story 70-1E-a: OCR-prefilled id fields (sent to backend on submit when available)
  const [idCardNumber, setIdCardNumber] = useState('');
  const [idCardExpiry, setIdCardExpiry] = useState('');
  const [scanBanner, setScanBanner] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Reset form when drawer opens
  useEffect(() => {
    if (open) {
      setType('INDIVIDUAL');
      setName(initialName);
      setTaxNumber('');
      setEmail('');
      setPhone('');
      setCity('');
      setIdCardNumber('');
      setIdCardExpiry('');
      setScanBanner(false);
      clear();
    }
  }, [open, initialName, clear]);

  /** Story 70-1E-a: handle OCR scan → prefill name + idCardNumber + idCardExpiry. */
  const handleScanned = useCallback((fields: ExtractedIdFields): void => {
    if (fields.surname || fields.givenNames) {
      const composed = [fields.surname, fields.givenNames].filter(Boolean).join(' ').trim();
      if (composed.length > 0) setName(composed);
    }
    if (fields.idCardNumber) setIdCardNumber(fields.idCardNumber);
    if (fields.idCardExpiry) setIdCardExpiry(fields.idCardExpiry);
    setScanBanner(true);
  }, []);

  // Debounced duplicate check on name/taxNumber change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void check({ name: name.trim(), taxNumber: taxNumber.trim() });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [name, taxNumber, check]);

  const handleSubmit = useCallback(async () => {
    try {
      const partner = await create({
        type,
        name: name.trim(),
        taxNumber: taxNumber.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        city: city.trim() || undefined,
        // Story 70-1E-a — only forward OCR-prefilled fields when set
        idCardNumber: idCardNumber.trim() || undefined,
        idCardExpiry: idCardExpiry.trim() || undefined,
      });
      toast.success(t('partner.quickCreate.success', { name: partner.name }));
      onOpenChange(false);
      onCreated(partner);
    } catch (err) {
      // Q-T155-FE: a nyers BE-szöveg helyett error-CODE-alapú magyar üzenet.
      // A pultos korábban azt olvasta, hogy „Record already exists" — miközben nem
      // a partner létezett, hanem a kód-generátor ütközött. A BE mostantól
      // strukturált code-ot ad (`PARTNER_DUPLICATE_TAX` / `PARTNER_DUPLICATE_CODE`,
      // mérve: prisma-partner.repository.ts), amit a `useApiError` az
      // `error.<CODE>` kulcsra fordít. Ismeretlen code esetén a helper a BE
      // üzenetére esik vissza — így új hibakód sem tünteti el a visszajelzést.
      toast.error(t('partner.quickCreate.error', { message: getErrorMessage(err) }));
    }
  }, [
    type,
    name,
    taxNumber,
    email,
    phone,
    city,
    idCardNumber,
    idCardExpiry,
    create,
    onOpenChange,
    onCreated,
    t,
    getErrorMessage,
  ]);

  const canSubmit = name.trim().length >= 2 && !isLoading;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-md overflow-y-auto border-l-2 border-primary/40 shadow-2xl dark:bg-gray-900"
        style={{ top: 0, bottom: 0 }}
      >
        <SheetHeader>
          <SheetTitle>{t('partner.quickCreate.title')}</SheetTitle>
          <SheetDescription className="sr-only">{t('partner.quickCreate.title')}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4 px-1">
          {/* Story 70-1E-a: scan trigger + success banner at the top */}
          <div className="flex items-center justify-end">
            <ScanIdCardButton
              onScanned={handleScanned}
              variant="outline"
              data-testid="quick-create-scan-id"
            />
          </div>
          {scanBanner && (
            <div
              role="status"
              className="rounded-none border-2 p-2 text-sm"
              style={{
                borderColor: '#14B8A6',
                backgroundColor: 'rgba(20, 184, 166, 0.08)',
                color: '#0d7d6b',
              }}
              data-testid="quick-create-scan-success"
            >
              {t('partner.scan.success')}
            </div>
          )}

          {/* Type radio */}
          <div>
            <Label>{t('partner.quickCreate.typeLabel')}</Label>
            <div className="mt-2 flex gap-3">
              {(['INDIVIDUAL', 'COMPANY'] as const).map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setType(v)}
                  className={`rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors ${
                    type === v
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-muted-foreground'
                  }`}
                >
                  {t(
                    v === 'INDIVIDUAL'
                      ? 'partner.quickCreate.individual'
                      : 'partner.quickCreate.company'
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <Label htmlFor="qc-name">{t('partner.quickCreate.nameLabel')}</Label>
            <Input
              id="qc-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t('partner.quickCreate.namePlaceholder')}
              autoFocus
            />
          </div>

          {/* Tax Number + hint */}
          <div>
            <Label htmlFor="qc-tax">{t('partner.quickCreate.taxNumberLabel')}</Label>
            <Input
              id="qc-tax"
              value={taxNumber}
              onChange={e => setTaxNumber(e.target.value)}
              placeholder={t('partner.quickCreate.taxNumberPlaceholder')}
            />
            {type === 'COMPANY' && !taxNumber.trim() && (
              <p className="mt-1 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <span>⚠️</span> {t('partner.quickCreate.taxNumberHint')}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="qc-email">{t('partner.quickCreate.emailLabel')}</Label>
            <Input
              id="qc-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('partner.quickCreate.emailPlaceholder')}
            />
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="qc-phone">{t('partner.quickCreate.phoneLabel')}</Label>
            <Input
              id="qc-phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={t('partner.quickCreate.phonePlaceholder')}
            />
          </div>

          {/* City */}
          <div>
            <Label htmlFor="qc-city">{t('partner.quickCreate.cityLabel')}</Label>
            <Input
              id="qc-city"
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder={t('partner.quickCreate.cityPlaceholder')}
            />
          </div>

          {/* Story 70-1E-a: OCR-prefilled id fields (INDIVIDUAL only) */}
          {type === 'INDIVIDUAL' && (
            <>
              <div>
                <Label htmlFor="qc-idnum">{t('partner.idCardNumber')}</Label>
                <Input
                  id="qc-idnum"
                  value={idCardNumber}
                  onChange={e => setIdCardNumber(e.target.value)}
                  data-testid="quick-create-id-number"
                />
              </div>
              <div>
                <Label htmlFor="qc-idexp">{t('partner.idCardExpiry') || 'Okmány lejárata'}</Label>
                <Input
                  id="qc-idexp"
                  type="date"
                  value={idCardExpiry}
                  onChange={e => setIdCardExpiry(e.target.value)}
                  data-testid="quick-create-id-expiry"
                />
              </div>
            </>
          )}

          {/* Duplicate warning */}
          {duplicates.length > 0 && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                {t('partner.quickCreate.duplicateWarning')}
              </p>
              <ul className="mt-1 space-y-1">
                {duplicates.map(d => (
                  <li key={d.id} className="text-sm text-amber-700 dark:text-amber-400">
                    {d.name} {d.taxNumber ? `(${d.taxNumber})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <SheetFooter className="mt-6 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('partner.quickCreate.cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isLoading ? '...' : t('partner.quickCreate.submit')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
